<template>
	<div v-if="showPreview" class="preview-modal" @click="$emit('close-preview')">
		<div
			class="preview-content"
			:class="{ 'preview-content--with-sidebar': hasSidebar }"
			@click.stop
		>
			<div class="preview-main">
				<div class="preview-body">
					<template v-if="isPdfPreview">
						<div v-if="pdfLoading" class="preview-loading">
							{{ generatedPdfUrl || pdfUrl ? 'Loading PDF…' : 'Generating PDF preview…' }}
						</div>
						<div v-else-if="pdfError" class="preview-error">{{ pdfError }}</div>
						<div ref="pdfContainer" class="pdf-preview-container"></div>
					</template>
					<div v-else ref="htmlContainer" class="preview-html" v-html="invoiceHtml"></div>
				</div>
				<aside v-if="hasSidebar" class="preview-sidebar">
					<slot name="sidebar" />
				</aside>
			</div>
			<div class="preview-footer">
				<button @click="$emit('close-preview')" class="preview-btn">
					<AppointmentIcon name="close" :size="18" />
					<span>Close</span>
				</button>
				<button
					@click="$emit('download-pdf')"
					class="preview-btn preview-btn--primary"
					:disabled="pdfLoading"
				>
					<AppointmentIcon name="download" :size="18" />
					<span>Download PDF</span>
				</button>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref, useSlots, watch } from 'vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf';
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.min?url';
import { paginateClientDocumentHtml } from '@/utils/clientDocumentPagination';
import { generateClientDocumentPdfFileFromHtml } from '@/utils/proposalDocument';

GlobalWorkerOptions.workerSrc = pdfWorker;

const props = defineProps({
	showPreview: Boolean,
	invoiceHtml: String,
	pdfUrl: {
		type: String,
		default: '',
	},
	renderHtmlAsPdf: {
		type: Boolean,
		default: false,
	},
	pdfFilename: {
		type: String,
		default: 'document.pdf',
	},
});

defineEmits(['close-preview', 'download-pdf']);

const slots = useSlots();
const pdfContainer = ref(null);
const htmlContainer = ref(null);
const pdfLoading = ref(false);
const pdfError = ref('');
const generatedPdfUrl = ref('');
const isPdfPreview = computed(() => Boolean(props.pdfUrl || props.renderHtmlAsPdf));
const hasSidebar = computed(() => Boolean(slots.sidebar));
let renderToken = 0;
let htmlRenderToken = 0;
let generationToken = 0;
let activeLoadingTask = null;
let activePdfDoc = null;
// The source and column width the pages were last drawn for; a new width redraws them.
let renderedSource = '';
let renderedWidth = 0;

const releaseGeneratedPdfUrl = () => {
	if (!generatedPdfUrl.value) return;
	URL.revokeObjectURL(generatedPdfUrl.value);
	generatedPdfUrl.value = '';
};

const clearPdf = () => {
	if (pdfContainer.value) {
		pdfContainer.value.innerHTML = '';
	}
	if (activeLoadingTask) {
		activeLoadingTask.destroy?.();
		activeLoadingTask = null;
	}
	if (activePdfDoc) {
		activePdfDoc.destroy();
		activePdfDoc = null;
	}
};

const renderPdf = async (sourceUrl) => {
	if (!sourceUrl || !props.showPreview) return;
	const currentToken = ++renderToken;
	pdfLoading.value = true;
	pdfError.value = '';

	await nextTick();
	if (!pdfContainer.value) return;
	pdfContainer.value.innerHTML = '';

	try {
		const loadingTask = getDocument({ url: sourceUrl });
		activeLoadingTask = loadingTask;
		const pdf = await loadingTask.promise;
		if (activeLoadingTask === loadingTask) {
			activeLoadingTask = null;
		}
		if (currentToken !== renderToken) {
			pdf.destroy();
			return;
		}

		activePdfDoc = pdf;
		const totalPages = pdf.numPages || 0;
		const containerWidth = pdfContainer.value.clientWidth || 0;
		const availableWidth = Math.max(0, containerWidth - 32);
		renderedSource = sourceUrl;
		renderedWidth = containerWidth;

		for (let pageNum = 1; pageNum <= totalPages; pageNum += 1) {
			const page = await pdf.getPage(pageNum);
			if (currentToken !== renderToken || !pdfContainer.value) return;
			const unscaledViewport = page.getViewport({ scale: 1 });
			// The page fills its column: larger on a wide screen, smaller beside the details on a tablet.
			const scale = availableWidth
				? Math.min(2, Math.max(0.4, availableWidth / unscaledViewport.width))
				: 1.2;
			const viewport = page.getViewport({ scale });

			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d');
			const outputScale = window.devicePixelRatio || 1;
			canvas.width = Math.floor(viewport.width * outputScale);
			canvas.height = Math.floor(viewport.height * outputScale);
			canvas.style.width = `${viewport.width}px`;
			canvas.style.height = `${viewport.height}px`;
			if (context && outputScale !== 1) {
				context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
			}

			const wrapper = document.createElement('div');
			wrapper.className = 'pdf-page';
			wrapper.appendChild(canvas);
			pdfContainer.value.appendChild(wrapper);

			await page.render({ canvasContext: context, viewport }).promise;
			if (currentToken !== renderToken) return;
		}
	} catch (error) {
		if (currentToken === renderToken) {
			pdfError.value = 'Failed to load PDF preview.';
		}
	} finally {
		if (currentToken === renderToken) {
			pdfLoading.value = false;
		}
	}
};

const renderGeneratedHtmlPdf = async (currentToken) => {
	if (!props.invoiceHtml || !props.showPreview) return;
	pdfLoading.value = true;
	pdfError.value = '';

	await nextTick();
	if (currentToken !== generationToken || !props.showPreview) return;

	try {
		const file = await generateClientDocumentPdfFileFromHtml({
			html: props.invoiceHtml,
			filename: props.pdfFilename,
		});
		if (currentToken !== generationToken || !props.showPreview) return;

		releaseGeneratedPdfUrl();
		generatedPdfUrl.value = URL.createObjectURL(file);
		await renderPdf(generatedPdfUrl.value);
	} catch (error) {
		if (currentToken !== generationToken) return;
		pdfLoading.value = false;
		pdfError.value = 'Failed to generate PDF preview.';
	}
};

const renderHtmlPreview = async () => {
	if (isPdfPreview.value || !props.showPreview) {
		htmlRenderToken += 1;
		return;
	}
	const currentToken = ++htmlRenderToken;
	await nextTick();
	if (currentToken !== htmlRenderToken || !htmlContainer.value) return;
	await paginateClientDocumentHtml(htmlContainer.value);
};

watch(
	() => [
		props.invoiceHtml,
		props.showPreview,
		props.pdfUrl,
		props.renderHtmlAsPdf,
		props.pdfFilename,
	],
	() => {
		renderToken += 1;
		htmlRenderToken += 1;
		generationToken += 1;
		clearPdf();
		releaseGeneratedPdfUrl();
		pdfLoading.value = false;
		pdfError.value = '';

		if (!props.showPreview) return;
		if (props.pdfUrl) {
			void renderPdf(props.pdfUrl);
			return;
		}
		if (props.renderHtmlAsPdf) {
			void renderGeneratedHtmlPdf(generationToken);
			return;
		}
		void renderHtmlPreview();
	},
	{ immediate: true, flush: 'post' },
);

// Turning a tablet (or resizing the window) redraws the pages for the new column width.
let resizeObserver = null;
let resizeTimer = null;
watch(pdfContainer, (container) => {
	resizeObserver?.disconnect();
	resizeObserver = null;
	if (!container || typeof ResizeObserver === 'undefined') return;
	resizeObserver = new ResizeObserver(() => {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(() => {
			if (!pdfContainer.value || pdfLoading.value || !renderedSource) return;
			if (Math.abs((pdfContainer.value.clientWidth || 0) - renderedWidth) < 24) return;
			void renderPdf(renderedSource);
		}, 150);
	});
	resizeObserver.observe(container);
});

onUnmounted(() => {
	renderToken += 1;
	htmlRenderToken += 1;
	generationToken += 1;
	clearTimeout(resizeTimer);
	resizeObserver?.disconnect();
	clearPdf();
	releaseGeneratedPdfUrl();
});
</script>

<style scoped>
.preview-modal {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(0, 0, 0, 0.8);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
}

.preview-content {
	background: white;
	border-radius: 0;
	width: 90%;
	max-width: 865px;
	margin: 10px 0;
	height: calc(100% - 20px);
	max-height: calc(100% - 20px);
	display: flex;
	flex-direction: column;
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.preview-content--with-sidebar {
	max-width: 1280px;
}

.preview-main {
	flex: 1;
	display: flex;
	min-height: 0;
}

.preview-body {
	flex: 1;
	overflow: auto;
	padding: 24px 32px;
	background-color: #f5f5f5;
	border-top-left-radius: 0;
	border-top-right-radius: 0;
}

.preview-sidebar {
	width: 320px;
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	padding: 24px 20px;
	border-left: 1px solid #e5e7eb;
	background: #fff;
	overflow: auto;
}

/* Ensure the injected HTML respects its container */
.preview-body :deep(.pdf-gen-invoice-container) {
	margin: 0 auto 24px;
	border: 1px solid rgba(0, 0, 0, 0.06);
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
}

.preview-body :deep(.pdf-gen-invoice-container:last-child) {
	margin-bottom: 0;
}

.preview-loading,
.preview-error {
	color: var(--text-color-secondary, #6b7280);
	text-align: center;
	padding: 24px;
}

.pdf-preview-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;
	padding: 8px 0 24px;
	max-width: 900px;
	margin: 0 auto;
}

.pdf-page {
	background: #fff;
	border: 1px solid rgba(0, 0, 0, 0.06);
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
	padding: 8px;
}

.preview-footer {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 20px;
	padding: 12px 24px;
	border-top: 1px solid #e0e0e0;
	background: #f5f5f5;
}

.preview-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 7px;
	border: none;
	background: transparent;
	color: var(--noc-blue, #0065ff);
	cursor: pointer;
	font-weight: 600;
	padding: 0;
	border-radius: 0;
	height: 32px;
	min-height: 32px;
	box-shadow: none;
	font-size: 0.86rem;
	font-family: inherit;
	white-space: nowrap;
	transition: color 0.1s ease, opacity 0.1s ease;
}

.preview-btn:hover:not(:disabled) {
	opacity: 0.82;
}

.preview-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.preview-btn:focus-visible {
	outline: 2px solid rgba(0, 101, 255, 0.28);
	outline-offset: 2px;
}

.preview-btn:active:not(:disabled) {
	transform: none;
}

.preview-btn--primary {
	font-weight: 600;
}

/* A tablet lying on its side (or any narrow landscape window) keeps the invoice on the left and
   its details on the right, as a wide screen does; the preview takes nearly the whole screen. */
@media (max-width: 1100px) and (orientation: landscape) {
	.preview-content--with-sidebar {
		width: calc(100% - 24px);
		max-width: none;
	}

	.preview-content--with-sidebar .preview-body {
		padding: 16px;
	}

	.preview-sidebar {
		width: 300px;
		padding: 20px 16px;
	}
}

/* Standing upright (a tablet in portrait, a phone), the details go under the invoice. */
@media (max-width: 1100px) and (orientation: portrait) {
	.preview-content--with-sidebar {
		max-width: 95%;
	}

	.preview-main {
		flex-direction: column;
	}

	.preview-sidebar {
		width: 100%;
		border-left: none;
		border-top: 1px solid #e5e7eb;
	}
}
</style>
